import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import { X, Send, Loader2, MessageSquare, Paperclip, FileText, Image, File } from 'lucide-react'
import { toast } from 'react-toastify'
import { useExchangeMessages, useSendMessage, useMarkAsRead, useUnreadCount, getAttachmentUrl, MAX_FILE_SIZE, MAX_FILES, ALLOWED_TYPES } from '../hooks'
import type { ExchangeLevel, Attachment } from '../hooks/use-exchange-messages'
import type { ExchangeMessage } from '../hooks/use-exchange-messages'
import { cn } from '@/shared/lib/utils'

interface ExchangePanelProps {
  isOpen: boolean
  onClose: () => void
  level: ExchangeLevel
  scopeId: string
  title: string
  subtitle?: string
}

function formatTime(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (date.toDateString() === today.toDateString()) {
    return 'Today'
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday'
  } else {
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
  }
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return Image
  if (mimeType === 'application/pdf') return FileText
  return File
}

function AttachmentItem({ attachment }: { attachment: Attachment }) {
  const [url, setUrl] = useState<string>('')
  const FileIcon = getFileIcon(attachment.mime_type)
  const isImage = attachment.mime_type.startsWith('image/')

  useEffect(() => {
    getAttachmentUrl(attachment.storage_path).then(setUrl)
  }, [attachment.storage_path])

  if (!url) return null

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'flex items-center gap-2 rounded-lg border border-border bg-white p-2 transition-colors hover:bg-background-secondary',
        isImage && 'flex-col items-start'
      )}
    >
      {isImage ? (
        <img
          src={url}
          alt={attachment.file_name}
          className="max-h-32 max-w-full rounded object-contain"
        />
      ) : (
        <FileIcon className="h-5 w-5 flex-shrink-0 text-foreground-muted" />
      )}
      <div className="flex flex-col overflow-hidden">
        <span className="truncate text-xs font-medium text-foreground">
          {attachment.file_name}
        </span>
        <span className="text-[10px] text-foreground-muted">
          {formatFileSize(attachment.file_size)}
        </span>
      </div>
    </a>
  )
}

function MessageBubble({ message }: { message: ExchangeMessage }) {
  const isEbuilder = message.is_from_ebuilder
  const authorName = message.author?.full_name || message.author?.email || 'Unknown'
  const hasAttachments = message.attachments && message.attachments.length > 0

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2 text-[11px] text-foreground-muted">
        {isEbuilder ? (
          <>
            <span className="font-medium text-accent">HeyNicolai team</span>
            <span>·</span>
            <span>{formatTime(message.created_at)}</span>
          </>
        ) : (
          <>
            <span className="font-medium">{authorName}</span>
            <span>·</span>
            <span>{formatTime(message.created_at)}</span>
          </>
        )}
      </div>
      <div
        className={cn(
          'rounded-xl px-4 py-3 text-sm',
          isEbuilder
            ? 'rounded-tl-none bg-orange-50 text-foreground'
            : 'rounded-tl-none bg-background-secondary text-foreground'
        )}
      >
        {message.content && (
          <div className="prose prose-sm max-w-none prose-p:my-0 prose-p:leading-relaxed prose-strong:font-semibold prose-ul:my-1 prose-ol:my-1 prose-li:my-0">
            <ReactMarkdown>{message.content}</ReactMarkdown>
          </div>
        )}
        {hasAttachments && (
          <div className={cn('flex flex-wrap gap-2', message.content && 'mt-3')}>
            {message.attachments!.map((attachment) => (
              <AttachmentItem key={attachment.id} attachment={attachment} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function groupMessagesByDate(messages: ExchangeMessage[]): Map<string, ExchangeMessage[]> {
  const groups = new Map<string, ExchangeMessage[]>()

  for (const message of messages) {
    const dateKey = formatDate(message.created_at)
    if (!groups.has(dateKey)) {
      groups.set(dateKey, [])
    }
    groups.get(dateKey)!.push(message)
  }

  return groups
}

export function ExchangePanel({ isOpen, onClose, level, scopeId, title, subtitle }: ExchangePanelProps) {
  const { t } = useTranslation()
  const [newMessage, setNewMessage] = useState('')
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data: messages = [], isLoading } = useExchangeMessages(level, scopeId)
  const sendMutation = useSendMessage()
  const markAsReadMutation = useMarkAsRead()

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  // Mark as read when panel opens
  useEffect(() => {
    if (isOpen && scopeId) {
      markAsReadMutation.mutate({ level, scopeId })
    }
  }, [isOpen, level, scopeId])

  // Scroll to bottom when messages change
  useEffect(() => {
    if (isOpen) {
      scrollToBottom()
    }
  }, [messages, isOpen])

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])

    // Validate files
    const validFiles: File[] = []
    for (const file of files) {
      if (selectedFiles.length + validFiles.length >= MAX_FILES) {
        toast.error(t('exchange.maxFilesError', { max: MAX_FILES }))
        break
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(t('exchange.fileTooLarge', { name: file.name, max: MAX_FILE_SIZE / 1024 / 1024 }))
        continue
      }
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(t('exchange.fileTypeNotAllowed', { name: file.name }))
        continue
      }
      validFiles.push(file)
    }

    setSelectedFiles((prev) => [...prev, ...validFiles])

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSend = () => {
    if (!newMessage.trim() && selectedFiles.length === 0) return

    sendMutation.mutate(
      { level, scopeId, content: newMessage.trim(), files: selectedFiles },
      {
        onSuccess: () => {
          setNewMessage('')
          setSelectedFiles([])
        },
        onError: (error) => {
          toast.error(error.message)
        },
      }
    )
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      handleSend()
    }
  }

  const messageGroups = groupMessagesByDate(messages)
  const hasContent = newMessage.trim().length > 0 || selectedFiles.length > 0
  const canSend = hasContent && !sendMutation.isPending

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/20 transition-opacity"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative flex h-full w-[400px] flex-col bg-white shadow-xl animate-in slide-in-from-right duration-300">
        {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <div className="flex flex-col gap-0.5">
          <span className="text-[15px] font-semibold text-foreground">{title}</span>
          {subtitle && (
            <span className="text-xs text-foreground-muted">{subtitle}</span>
          )}
        </div>
        <button
          onClick={onClose}
          className="flex h-7 w-7 items-center justify-center rounded-md bg-background-secondary text-foreground-muted transition-colors hover:bg-slate-200 hover:text-foreground"
        >
          <X className="h-[18px] w-[18px]" />
        </button>
      </div>

      {/* Messages Area */}
      <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-5">
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-foreground-muted" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-1 items-center justify-center">
            <p className="text-center text-sm text-foreground-muted">
              {t('exchange.noMessages')}
            </p>
          </div>
        ) : (
          <>
            {Array.from(messageGroups.entries()).map(([date, msgs]) => (
              <div key={date} className="flex flex-col gap-4">
                {/* Date separator */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 border-t border-border" />
                  <span className="text-[11px] text-foreground-muted">{date}</span>
                  <div className="flex-1 border-t border-border" />
                </div>
                {/* Messages for this date */}
                {msgs.map((message) => (
                  <MessageBubble key={message.id} message={message} />
                ))}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="flex flex-col gap-3 border-t border-border p-5">
        {/* Selected files preview */}
        {selectedFiles.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedFiles.map((file, index) => {
              const FileIcon = getFileIcon(file.type)
              return (
                <div
                  key={`${file.name}-${index}`}
                  className="flex items-center gap-2 rounded-lg border border-border bg-background-secondary px-3 py-2"
                >
                  <FileIcon className="h-4 w-4 flex-shrink-0 text-foreground-muted" />
                  <span className="max-w-[120px] truncate text-xs text-foreground">
                    {file.name}
                  </span>
                  <button
                    onClick={() => handleRemoveFile(index)}
                    className="flex h-4 w-4 items-center justify-center rounded-full text-foreground-muted hover:bg-slate-200 hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )
            })}
          </div>
        )}

        {/* Input row */}
        <div className="flex gap-2">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ALLOWED_TYPES.join(',')}
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Attach button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={sendMutation.isPending || selectedFiles.length >= MAX_FILES}
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-border bg-white text-foreground-muted transition-colors hover:bg-background-secondary hover:text-foreground disabled:opacity-50"
            title={t('exchange.attachFile')}
          >
            <Paperclip className="h-5 w-5" />
          </button>

          {/* Message input */}
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('exchange.messagePlaceholder')}
            disabled={sendMutation.isPending}
            className="flex-1 rounded-lg border border-border bg-background-secondary px-4 py-3 text-sm placeholder:text-foreground-subtle focus:border-accent focus:outline-none disabled:opacity-50"
          />

          {/* Send button */}
          <button
            onClick={handleSend}
            disabled={!canSend}
            className={cn(
              'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-white transition-colors',
              canSend
                ? 'bg-accent hover:bg-accent/90'
                : 'bg-accent opacity-50'
            )}
            title={t('exchange.sendMessage')}
          >
            {sendMutation.isPending ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </button>
        </div>

        {/* Hint */}
        <p className="text-[10px] text-foreground-subtle">
          {t('exchange.sendHint')}
        </p>
      </div>
      </div>
    </div>
  )
}

// Discussion Button component with badge
interface DiscussionButtonProps {
  level: ExchangeLevel
  scopeId: string | undefined
  onClick: () => void
}

export function DiscussionButton({ level, scopeId, onClick }: DiscussionButtonProps) {
  const { t } = useTranslation()
  const { data: unreadCount = 0 } = useUnreadCount(level, scopeId)

  return (
    <button
      onClick={onClick}
      className="flex h-10 items-center gap-2 rounded-lg border border-border bg-white px-4 text-sm font-medium text-foreground transition-colors hover:bg-background-secondary"
    >
      <MessageSquare className="h-4 w-4" />
      <span>{t('exchange.title')}</span>
      {unreadCount > 0 && (
        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1.5 text-[11px] font-semibold text-white">
          {unreadCount}
        </span>
      )}
    </button>
  )
}
